import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useNavigate } from 'react-router';
import { z } from 'zod';

import { ApiError, NetworkError } from '../auth/api.ts';
import { AuthLayout, FormError } from '../auth/AuthLayout.tsx';
import { useAuth } from '../auth/AuthProvider.tsx';
import { errorCopy, OFFLINE_COPY } from '../auth/errorCopy.ts';
import { Button, Field, PasswordInput, TextInput } from '../components/index.ts';

const schema = z.object({
  email: z
    .string()
    .trim()
    .min(1, 'Enter your email address.')
    .email('Enter a valid email address.'),
  // No length rule on sign-in. The requirement belongs on the account you are
  // creating, not on the one you already have.
  password: z.string().min(1, 'Enter your password.'),
});

type Values = z.infer<typeof schema>;

export function LoginRoute() {
  const { signIn } = useAuth();
  const navigate = useNavigate();
  const [formError, setFormError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: { email: '', password: '' },
  });

  const onSubmit = handleSubmit(async (values) => {
    setFormError(null);
    try {
      await signIn(values);
      void navigate('/entries', { replace: true });
    } catch (error) {
      setFormError(
        error instanceof NetworkError
          ? OFFLINE_COPY
          : errorCopy(error instanceof ApiError ? error.code : null),
      );
    }
  });

  return (
    <AuthLayout
      title="Sign in"
      footer={
        <>
          No account yet?{' '}
          <Link to="/register" className="text-accent underline hover:text-accent-hover">
            Create one
          </Link>
        </>
      }
    >
      <form onSubmit={(event) => void onSubmit(event)} className="flex flex-col gap-4" noValidate>
        <FormError message={formError} />

        <Field label="Email" error={errors.email?.message}>
          <TextInput
            type="email"
            autoComplete="email"
            autoCapitalize="none"
            spellCheck={false}
            {...register('email')}
          />
        </Field>

        <Field label="Password" error={errors.password?.message}>
          <PasswordInput autoComplete="current-password" {...register('password')} />
        </Field>

        <Button type="submit" variant="primary" block loading={isSubmitting}>
          Sign in
        </Button>

        <p className="text-center text-sm">
          <Link to="/forgot-password" className="text-muted underline hover:text-text">
            Forgotten your password?
          </Link>
        </p>
      </form>
    </AuthLayout>
  );
}
